/**
 * VersionTimeline — Visual history of track versions
 * Shows v1, v2, v3... with ability to restore and compare
 */
import React, { useState } from 'react';
import { Clock, RotateCcw, Play, Pause, ChevronDown, ChevronUp, GitBranch } from 'lucide-react';

export function VersionTimeline({ 
  versions = [], 
  currentVersion = 1,
  onRestore,
  onPreview,
  compact = false,
  className = '',
}) {
  const [expanded, setExpanded] = useState(false);
  const [previewingVersion, setPreviewingVersion] = useState(null);
  
  if (versions.length === 0) {
    return null;
  }
  
  const displayVersions = expanded ? versions : versions.slice(-3);
  
  return (
    <div className={`bg-[#0A0A0B] rounded-xl border border-[#27272A] ${className}`} data-testid="version-timeline">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#0F0F12] transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-2">
          <GitBranch size={12} className="text-[#F5C842]" />
          <span className="text-[10px] sm:text-xs font-medium text-white">
            Versiones ({versions.length})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-[#52525B]">
            Actual: v{currentVersion}
          </span>
          {versions.length > 3 && (
            expanded ? <ChevronUp size={12} className="text-[#52525B]" /> : <ChevronDown size={12} className="text-[#52525B]" />
          )}
        </div>
      </button>
      
      {/* Version list */}
      <div className="px-2 pb-2">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-[#27272A]" />
          
          {/* Versions */}
          <div className="space-y-1">
            {displayVersions.map((version, idx) => {
              const versionNum = version.version || idx + 1;
              const isCurrent = versionNum === currentVersion;
              const isPreviewing = previewingVersion === versionNum;
              const timestamp = version.timestamp ? new Date(version.timestamp) : null;
              
              return (
                <div
                  key={versionNum}
                  className={`
                    relative flex items-center gap-2 pl-6 pr-2 py-1.5 rounded-lg transition-all
                    ${isCurrent ? 'bg-[#F5C84215]' : 'hover:bg-[#0F0F12]'}
                  `}
                  data-testid={`version-${versionNum}`}
                >
                  {/* Version dot */}
                  <div 
                    className={`
                      absolute left-1 w-5 h-5 rounded-full border-2 flex items-center justify-center
                      ${isCurrent 
                        ? 'border-[#F5C842] bg-[#F5C84230]' 
                        : 'border-[#3F3F46] bg-[#0A0A0B]'
                      }
                    `}
                  >
                    <span className="text-[8px] font-bold" style={{ color: isCurrent ? '#F5C842' : '#52525B' }}>
                      {versionNum}
                    </span>
                  </div>
                  
                  {/* Version info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] font-medium ${isCurrent ? 'text-[#F5C842]' : 'text-white'}`}>
                        v{versionNum}
                      </span>
                      {isCurrent && (
                        <span className="text-[8px] px-1.5 py-0.5 bg-[#F5C84230] text-[#F5C842] rounded font-medium">
                          ACTUAL
                        </span>
                      )}
                    </div>
                    {version.instruction && (
                      <p className="text-[9px] text-[#52525B] truncate mt-0.5">
                        {version.instruction}
                      </p>
                    )}
                    {timestamp && (
                      <p className="text-[8px] text-[#3F3F46] flex items-center gap-1 mt-0.5">
                        <Clock size={8} />
                        {formatRelativeTime(timestamp)}
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {/* Preview button */}
                    {version.audio_url && (
                      <button
                        onClick={() => {
                          if (isPreviewing) {
                            setPreviewingVersion(null);
                            onPreview?.(version.audio_url, false, versionNum);
                          } else {
                            setPreviewingVersion(versionNum);
                            onPreview?.(version.audio_url, true, versionNum);
                          }
                        }}
                        className={`
                          w-6 h-6 rounded flex items-center justify-center transition-colors
                          ${isPreviewing 
                            ? 'bg-[#F5C842] text-black' 
                            : 'bg-[#1E1E23] text-[#A1A1AA] hover:text-white'
                          }
                        `}
                        title="A/B versión"
                      >
                        {isPreviewing ? <Pause size={10} /> : <Play size={10} className="ml-0.5" />}
                      </button>
                    )}
                    
                    {/* Restore button */}
                    {!isCurrent && (
                      <button
                        onClick={() => onRestore?.(versionNum - 1)}
                        className="w-6 h-6 rounded bg-[#1E1E23] text-[#A1A1AA] hover:text-white hover:bg-[#27272A] flex items-center justify-center transition-colors"
                        title="Restaurar esta versión"
                      >
                        <RotateCcw size={10} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Show more indicator */}
        {!expanded && versions.length > 3 && (
          <button
            onClick={() => setExpanded(true)}
            className="w-full mt-2 text-[9px] text-[#52525B] hover:text-[#A1A1AA] transition-colors text-center py-1"
          >
            Ver {versions.length - 3} versiones anteriores
          </button>
        )}
      </div>
    </div>
  );
}

// Compact version badge for header
export function VersionBadge({ version, totalVersions, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 px-2 py-1 bg-[#0F0F12] hover:bg-[#1E1E23] rounded border border-[#27272A] transition-colors"
      title="Ver historial de versiones"
    >
      <GitBranch size={10} className="text-[#F5C842]" />
      <span className="text-[9px] font-mono text-white">v{version}</span>
      {totalVersions > 1 && (
        <span className="text-[8px] text-[#52525B]">/{totalVersions}</span>
      )}
    </button>
  );
}

function formatRelativeTime(date) {
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'ahora mismo';
  if (minutes < 60) return `hace ${minutes}m`;
  if (hours < 24) return `hace ${hours}h`;
  if (days < 7) return `hace ${days}d`;
  return date.toLocaleDateString();
}

export default VersionTimeline;
